import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { getHouses, checkUserCanManageHouses } from '../../services/houseService';
import { getParticipants } from '../../services/participantService';
import { House, Participant } from '../../types/models';
import { Plus, Home, Download, FileSpreadsheet, FileText, File as FilePdf, LayoutDashboard, ExternalLink } from 'lucide-react';
import { format } from 'date-fns';
import { exportToExcel, exportToCSV, exportToPDF } from '../../utils/export';
import { useAuthStore } from '../../store/authStore';

const HouseList: React.FC = () => {
  const [houses, setHouses] = useState<House[]>([]);
  const [participants, setParticipants] = useState<Participant[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedHouse, setSelectedHouse] = useState<House | null>(null);
  const [showExportMenu, setShowExportMenu] = useState(false);
  const [canManageHouses, setCanManageHouses] = useState(false);
  const { t } = useTranslation('common');
  const { user } = useAuthStore();

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        const [housesData, participantsData, canManage] = await Promise.all([
          getHouses(),
          getParticipants(),
          checkUserCanManageHouses()
        ]);
        setHouses(housesData);
        setParticipants(participantsData);
        setCanManageHouses(canManage);
      } catch (error) {
        console.error('Error fetching data:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  const getHouseParticipants = (houseId: string) => {
    return participants.filter(p => p.house_id === houseId);
  };

  const handleExport = (format: 'excel' | 'csv' | 'pdf') => {
    if (!selectedHouse) return;
    
    const houseParticipants = getHouseParticipants(selectedHouse.house_id);
    switch (format) {
      case 'excel':
        exportToExcel(houseParticipants);
        break;
      case 'csv':
        exportToCSV(houseParticipants);
        break;
      case 'pdf':
        exportToPDF(houseParticipants);
        break;
    }
    setShowExportMenu(false);
  };

  return (
    <div className="space-y-6">
      <div className="flex flex-col md:flex-row md:items-center md:justify-between">
        <h1 className="text-2xl font-bold text-gray-900">{t('houses.title')}</h1>
        <div className="mt-3 md:mt-0 flex space-x-3">
          <Link
            to="/houses/performance"
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
          >
            <LayoutDashboard className="h-4 w-4 mr-2" />
            {t('houses.actions.housePerformanceDisplay')}
          </Link>
          
          {user && canManageHouses && (
            <Link
              to="/houses/new"
              className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
            >
              <Plus className="h-4 w-4 mr-2" />
              {t('houses.actions.addHouse')}
            </Link>
          )}
        </div>
      </div>

      <div className="bg-white shadow overflow-hidden rounded-lg">
        {loading ? (
          <div className="flex justify-center items-center h-32">
            <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-blue-500"></div>
          </div>
        ) : houses.length === 0 ? (
          <div className="text-center py-12">
            <Home className="mx-auto h-12 w-12 text-gray-400" />
            <h3 className="mt-2 text-sm font-medium text-gray-900">{t('houses.noHouses.title')}</h3>
            <p className="mt-1 text-sm text-gray-500">{t('houses.noHouses.description.noFilters')}</p>
            {user && canManageHouses && (
              <div className="mt-6">
                <Link
                  to="/houses/new"
                  className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700"
                >
                  <Plus className="h-4 w-4 mr-2" />
                  {t('houses.actions.addHouse')}
                </Link>
              </div>
            )}
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    {t('houses.table.houseName')}
                  </th>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    {t('houses.table.color')}
                  </th>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    {t('houses.table.students')}
                  </th>
                  <th scope="col" className="relative px-6 py-3">
                    <span className="sr-only">{t('houses.table.actions')}</span>
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {houses.map((house) => {
                  const houseParticipants = getHouseParticipants(house.house_id);
                  return (
                    <tr key={house.house_id} className="hover:bg-gray-50">
                      <td className="px-6 py-4 whitespace-nowrap">
                        <Link
                          to={`/houses/${house.house_id}/details`}
                          className="text-sm font-medium text-blue-600 hover:text-blue-800 cursor-pointer flex items-center"
                        >
                          <div 
                            className="h-4 w-4 rounded mr-2" 
                            style={{ backgroundColor: house.house_color }}
                          />
                          {house.house_name}
                        </Link>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center">
                          <span className="text-sm text-gray-600">{house.house_color}</span>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm text-gray-900">
                          {houseParticipants.length} {t('houses.table.students')}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                        <div className="flex justify-end space-x-3">
                          <Link
                            to={`/houses/${house.house_id}/details`}
                            className="text-blue-600 hover:text-blue-800 flex items-center"
                          >
                            <ExternalLink className="h-4 w-4 mr-1" />
                            {t('familyConnections.view')}
                          </Link>
                          {user && canManageHouses && (
                            <Link 
                              to={`/houses/${house.house_id}/edit`} 
                              className="text-indigo-600 hover:text-indigo-800"
                            >
                              {t('houses.actions.edit')}
                            </Link>
                          )}
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>

            {selectedHouse && (
              <div className="mt-8 px-6 pb-6">
                <div className="flex items-center justify-between mb-4">
                  <h2 className="text-lg font-medium text-gray-900">
                    {selectedHouse.house_name} {t('houses.table.students')}
                  </h2>
                  <div className="relative">
                    <button
                      onClick={() => setShowExportMenu(!showExportMenu)}
                      className="inline-flex items-center px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                    >
                      <Download className="h-4 w-4 mr-2" />
                      {t('houses.actions.export')}
                    </button>
                    
                    {showExportMenu && (
                      <div className="absolute right-0 mt-2 w-48 rounded-md shadow-lg bg-white ring-1 ring-black ring-opacity-5 z-10">
                        <div className="py-1" role="menu">
                          <button
                            onClick={() => handleExport('excel')}
                            className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 flex items-center"
                          >
                            <FileSpreadsheet className="h-4 w-4 mr-2" />
                            Export to Excel
                          </button>
                          <button
                            onClick={() => handleExport('csv')}
                            className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 flex items-center"
                          >
                            <FileText className="h-4 w-4 mr-2" />
                            Export to CSV
                          </button>
                          <button
                            onClick={() => handleExport('pdf')}
                            className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 flex items-center"
                          >
                            <FilePdf className="h-4 w-4 mr-2" />
                            Export to PDF
                          </button>
                        </div>
                      </div>
                    )}
                  </div>
                </div>

                <div className="bg-white shadow overflow-hidden sm:rounded-lg">
                  <table className="min-w-full divide-y divide-gray-200">
                    <thead className="bg-gray-50">
                      <tr>
                        <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          {t('participants.table.name')}
                        </th>
                        <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          {t('participants.table.tamilName')}
                        </th>
                        <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          {t('participants.table.dateOfBirth')}
                        </th>
                        <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          {t('participants.table.gender')}
                        </th>
                        <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          {t('participants.table.department')}
                        </th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                      {getHouseParticipants(selectedHouse.house_id).map((participant) => (
                        <tr key={participant.student_id} className="hover:bg-gray-50">
                          <td className="px-6 py-4 whitespace-nowrap">
                            <Link to={`/participants/${participant.student_id}`}>
                              <div className="text-sm font-medium text-blue-600 hover:text-blue-800">
                                {participant.first_name} {participant.last_name}
                              </div>
                            </Link>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="text-sm text-gray-900">
                              {participant.first_name_tamil} {participant.last_name_tamil}
                            </div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="text-sm text-gray-900">
                              {format(new Date(participant.date_of_birth), 'dd.MM.yyyy')}
                            </div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                              participant.gender === 'M' ? 'bg-blue-100 text-blue-800' : 'bg-pink-100 text-pink-800'
                            }`}>
                              {participant.gender === 'M' ? t('participants.filters.male') : t('participants.filters.female')}
                            </span>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="text-sm text-gray-900">
                              {participant.department?.name || '-'}
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default HouseList;